import { createApi } from "@reduxjs/toolkit/query/react";
import { getFastApiBaseUrl } from "../const/env";
import { authedFetchBaseQuery } from "./authedFetchBaseQuery";

const getBaseUrl = (): string => getFastApiBaseUrl();

/** Turn FastAPI /crm_agent (and similar) JSON into a single chat bubble string. */
function stripJsonFences(text: string): string {
  const s = (text || "").trim();
  if (!s.startsWith("```")) return s;
  const lines = s.split("\n");
  if (lines.length >= 2 && lines[0].startsWith("```")) lines.shift();
  if (lines.length >= 1 && lines[lines.length - 1].trim() === "```") lines.pop();
  return lines.join("\n").trim();
}

function extractEmailObjectsFromText(text: string): Record<string, unknown>[] {
  const src = stripJsonFences(text);
  const matches = src.match(/\{"subject":[\s\S]*?"body":"[\s\S]*?"\}/g) || [];
  const out: Record<string, unknown>[] = [];
  for (const m of matches) {
    try {
      const parsed = JSON.parse(m) as Record<string, unknown>;
      out.push(parsed);
    } catch {
      // Keep scanning other matches.
    }
  }
  return out;
}

function getEmailList(payload: unknown): Record<string, unknown>[] {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    return payload.filter((x): x is Record<string, unknown> => !!x && typeof x === "object");
  }
  if (typeof payload !== "object") return [];
  const obj = payload as Record<string, unknown>;
  const emails = obj.emails;
  if (Array.isArray(emails)) {
    return emails.filter((x): x is Record<string, unknown> => !!x && typeof x === "object");
  }
  if (emails && typeof emails === "object") {
    return [emails as Record<string, unknown>];
  }
  return [];
}

function emailsToMarkdown(emails: Record<string, unknown>[]): string {
  if (!emails.length) return "";
  return emails
    .map((email, i) => {
      const subject = String(email.subject || "(No subject)");
      const from = String(email.from || "Unknown sender");
      const date = String(email.date || "");
      const snippet = String(email.snippet || "");
      const body = String(email.body || "");
      return [
        `### ${i + 1}. ${subject}`,
        `- **From:** ${from}`,
        date ? `- **Date:** ${date}` : "",
        snippet ? `- **Snippet:** ${snippet}` : "",
        "",
        body ? body : "_No body preview available._",
      ]
        .filter(Boolean)
        .join("\n");
    })
    .join("\n\n---\n\n");
}

function formatSuccessChatText(
  res: Record<string, unknown>,
  isCrmAgent: boolean = false,
): string {
  const status = res?.status;
  const msg = typeof res?.message === "string" ? res.message : "";
  if (status !== "success") return msg;

  const state = res?.state as Record<string, unknown> | undefined;
  if (state && typeof state === "object") {
    const qOut = state["query_output"];
    if (isCrmAgent) {
      const qOutEmails = getEmailList(qOut);
      if (qOutEmails.length) return emailsToMarkdown(qOutEmails);
    }

    const qText = state["query_output_text"];
    if (typeof qText === "string" && qText.trim()) {
      if (isCrmAgent) {
        try {
          const parsed = JSON.parse(stripJsonFences(qText));
          const parsedEmails = getEmailList(parsed);
          if (parsedEmails.length) return emailsToMarkdown(parsedEmails);
        } catch {
          const extracted = extractEmailObjectsFromText(qText);
          if (extracted.length) return emailsToMarkdown(extracted);
        }
      }
      return qText.trim();
    }

    if (qOut !== undefined && qOut !== null) {
      try {
        return JSON.stringify(qOut, null, 2);
      } catch {
        return String(qOut);
      }
    }
    const err = state["error_message"];
    if (typeof err === "string" && err.trim()) {
      return `${msg}\n\n${err.trim()}`;
    }
  }
  return msg;
}

function formatMissingFieldsReport(rows: Array<Record<string, unknown>>): string {
  if (!rows.length) {
    return "Contacts with missing required fields were reported.";
  }
  return rows
    .map((row, index) => {
      const label =
        typeof row.contact === "string" && row.contact.trim()
          ? row.contact
          : typeof row.id === "string" && row.id.trim()
            ? row.id
            : `Row ${String(row.row_index ?? index + 1)}`;
      const missing = Array.isArray(row.missing_fields)
        ? row.missing_fields.map((x) => String(x).trim()).filter(Boolean).join(", ")
        : "";
      return missing ? `${label}: ${missing}` : `${label}: missing required fields`;
    })
    .join("\n");
}

function formatCustomerProfileAgentText(res: Record<string, unknown>): string {
  const base = formatSuccessChatText(res);
  const interruptPayload = res.interrupt_payload as Record<string, unknown> | undefined;
  const rows = interruptPayload?.rows_w_missing_fields;
  if (!Array.isArray(rows) || !rows.length) {
    return base;
  }
  const report = formatMissingFieldsReport(
    rows.filter((row) => !!row && typeof row === "object") as Array<Record<string, unknown>>,
  );
  return `${base}\n\n${report}`.trim();
}

function appendCreateEventPrefill(text: string, proposedEvent: unknown): string {
  if (!proposedEvent || typeof proposedEvent !== "object" || Array.isArray(proposedEvent)) {
    return text;
  }

  try {
    const encoded = encodeURIComponent(JSON.stringify(proposedEvent));
    return `${text}__EVENT_FORM_PREFILL__${encoded}__END_EVENT_FORM_PREFILL__`;
  } catch {
    return text;
  }
}

function appendGoalSetPrefill(text: string, prefill: unknown): string {
  if (!prefill || typeof prefill !== "object" || Array.isArray(prefill)) {
    return text;
  }
  try {
    const encoded = encodeURIComponent(JSON.stringify(prefill));
    return `${text}__GOALSET_FORM_PREFILL__${encoded}__END_GOALSET_FORM_PREFILL__`;
  } catch {
    return text;
  }
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: authedFetchBaseQuery({ baseUrl: getBaseUrl(), credentials: "include" }),
  tagTypes: ['Sessions', 'Chat', 'TabbedDict'],
  endpoints: (builder) => ({
    sendChatMessage: builder.mutation({
      query: ({ userEmail, threadId, humanInputMessage, initialSetup, session }: {
        userEmail: string;
        threadId: string;
        humanInputMessage: string | { content?: string; resume_payload?: Record<string, unknown> };
        initialSetup: boolean;
        session?: Record<string, unknown>;
      }) => {
        let endpoint = "/chat";
        let requestBody: Record<string, unknown> = {
          user_email: userEmail,
          thread_id: threadId || session?.thread_id,
          human_input_msg: humanInputMessage,
          initial_setup: initialSetup,
        };

        if (session && session.agent_type === "CreateEventAgent") {
          endpoint = "/sc_cal_agent";
          requestBody = {
            user_email: userEmail,
            thread_id: threadId || session.thread_id,
            human_message: typeof humanInputMessage === "string"
              ? { content: humanInputMessage }
              : (humanInputMessage as { content?: string })?.content
              ? { content: (humanInputMessage as { content: string }).content }
              : humanInputMessage || { content: "" },
            initial_setup: initialSetup,
            event_name: session?.event_name || "",
            start_time: session?.start_time || "",
            end_time: session?.end_time || "",
            scheduling_instructions: session?.scheduling_instructions || "",
            attendees: session?.attendees || [],
            color_id: session?.color_id || "",
            goal_name: session?.goal_name || "",
            goal_for_goal_desc: session?.goal_for_goal_desc || "",
            task_name: session?.task_name || "",
            goal_for_task_desc: session?.goal_for_task_desc || "",
            definition_of_done_for_task: session?.definition_of_done_for_task || "",
            description: session?.description || "",
            location: session?.location || "",
          };
        } else if (session && session.agent_type === "GoalSetAgent") {
          endpoint = "/goalsetagent";
          requestBody = {
            user_email: userEmail,
            thread_id: threadId || session.thread_id,
            human_message: typeof humanInputMessage === "string"
              ? { content: humanInputMessage }
              : (humanInputMessage as { content?: string })?.content
              ? { content: (humanInputMessage as { content: string }).content }
              : humanInputMessage || { content: "" },
            initial_setup: initialSetup,
            event_id: session?.event_id || "",
            event_summary: session?.event_summary || session?.title || "",
            attendee_email: session?.attendee_email || userEmail,
          };

          const fieldMappings: Record<string, unknown> = {
            "title": session?.title || session?.agent_type || "Goal Setting",
            "event_id": session?.event_id || "",
            "event_summary": session?.event_summary || "",
            "attendee_email": session?.attendee_email || userEmail,
            "first_node_done": session?.first_node_done || false,
            "unhandled": session?.unhandled ?? true,
            "goal_name": session?.goal_name || null,
            "task_name": session?.task_name || null,
            "color_id": session?.color_id || null,
            "id": session?.id || session?.color_id || null,
            "desc": session?.desc || null,
            "goal_for_goal": session?.goal_for_goal || null,
            "goal_for_task": session?.goal_for_task || null,
            "definition_of_done": session?.definition_of_done || null,
            "count": session?.count || null,
            "goal_hierarchy_stored": session?.goal_hierarchy_stored || false,
            "thread_id": session?.thread_id || threadId,
          };

          Object.keys(fieldMappings).forEach((field) => {
            requestBody[field] = fieldMappings[field];
          });
        } else if (session && session.agent_type === "BloggingAgent") {
          endpoint = "/blogging_agent";
          requestBody = {
            user_email: userEmail,
            thread_id: threadId || session.thread_id,
            human_message: typeof humanInputMessage === "string"
              ? { content: humanInputMessage }
              : (humanInputMessage as { content?: string })?.content
              ? { content: (humanInputMessage as { content: string }).content }
              : humanInputMessage || { content: "" },
            initial_setup: false,
          };
        } else if (session && session.agent_type === "FollowUpAgent") {
          endpoint = "/follow_up_agent";
          requestBody = {
            user_email: userEmail,
            thread_id: threadId || session.thread_id,
            human_message: typeof humanInputMessage === "string"
              ? { content: humanInputMessage }
              : (humanInputMessage as { content?: string })?.content
              ? { content: (humanInputMessage as { content: string }).content }
              : humanInputMessage || { content: "" },
            initial_setup: false,
          };

          const fieldMappings: Record<string, unknown> = {
            "title": session?.title || session?.agent_type || "Follow-up Event",
            "event_id": session?.event_id || "",
            "description": session?.description || "Follow-up for scheduled event",
            "scheduled_time": session?.scheduled_time || "",
            "reminder_type": session?.reminder_type || "calendar",
            "activated": session?.activated || false,
            "activated_at": session?.activated_at || null,
            "follow_up_message": session?.follow_up_message || "",
            "completed": session?.completed || false,
            "completed_at": session?.completed_at || null,
            "created_at": session?.created_at || "",
            "updated_at": session?.updated_at || session?.last_updated || "",
            "thread_id": session?.thread_id || threadId,
            "goal_id": session?.goal_id || null,
            "error": session?.error || null,
            "attendee_email": session?.attendee_email || (session?.attendee as { email?: string })?.email || session?.user || userEmail,
            "attendee": session?.attendee || {
              "email": session?.attendee_email || session?.user || userEmail,
              "responseStatus": "accepted"
            },
          };

          Object.keys(fieldMappings).forEach((field) => {
            requestBody[field] = fieldMappings[field];
          });
        } else if (session && session.agent_type === "CRMSendMessageAgent") {
          endpoint = "/crm_send_message_agent";
          const humanText =
            typeof humanInputMessage === "string"
              ? humanInputMessage
              : (humanInputMessage as { content?: string })?.content ?? "";
          const custom = session.crm_send_message as Record<string, unknown> | undefined;
          requestBody = {
            message:
              custom && typeof custom === "object" && custom !== null
                ? custom
                : {
                    to: String(session.crm_to ?? ""),
                    text: humanText,
                    ...(session.crm_from ? { from: session.crm_from } : {}),
                  },
          };
        } else if (session && session.agent_type === "ReceiveMessageAgent") {
          endpoint = "/receive_message_agent";
          const humanText =
            typeof humanInputMessage === "string"
              ? humanInputMessage
              : (humanInputMessage as { content?: string })?.content ?? "";
          const sessionEmailPrompt = session.email_agent_prompt as string | undefined;
          const arcadeUserId = "jobinthomas726@gmail.com";
          const email_agent_prompt =
            (sessionEmailPrompt && String(sessionEmailPrompt).trim()) ||
            (humanText.trim() || undefined);

          requestBody = {
            thread_id: threadId || session?.thread_id,
            user_id: arcadeUserId,
            email_agent_prompt: email_agent_prompt,
          };
        } else if (
          session &&
          (session.agent_type === "CustomerProfileAgent" ||
            (session.agent_type === "PlanMessageAgent" &&
              session.notification_type === "contacts_missing_required_fields"))
        ) {
          endpoint = "/customer_profile_endpoint";
          const humanText =
            typeof humanInputMessage === "string"
              ? humanInputMessage
              : (humanInputMessage as { content?: string })?.content ?? "";
          const resumePayload =
            typeof humanInputMessage === "object" && humanInputMessage !== null
              ? (humanInputMessage as { resume_payload?: Record<string, unknown> }).resume_payload
              : undefined;

          requestBody = {
            user_email: userEmail,
            thread_id: threadId || session?.thread_id,
            initial_setup: initialSetup,
            human_message: { content: humanText },
            ...(resumePayload && typeof resumePayload === "object"
              ? { resume_payload: resumePayload }
              : {}),
          };
        } else if (session && session.agent_type === "PlanMessageAgent") {
          endpoint = "/plan_message_agent";
          const humanText =
            typeof humanInputMessage === "string"
              ? humanInputMessage
              : (humanInputMessage as { content?: string })?.content ?? "";
          const resumePayload =
            typeof humanInputMessage === "object" && humanInputMessage !== null
              ? (humanInputMessage as { resume_payload?: Record<string, unknown> }).resume_payload
              : undefined;

          requestBody = {
            user_email: userEmail,
            thread_id: threadId || session?.thread_id,
            initial_setup: initialSetup,
            human_message: { content: humanText },
            ...(resumePayload && typeof resumePayload === "object"
              ? { resume_payload: resumePayload }
              : {}),
          };
        }

        return {
          url: endpoint,
          method: 'POST',
          body: requestBody,
        };
      },
      transformResponse: (
        response: unknown,
        _meta: unknown,
        arg: { session?: Record<string, unknown> },
      ) => {
        const res = response as Record<string, unknown>;
        if (res?.status === "success") {
          const isCrmAgent = arg?.session?.agent_type === "ReceiveMessageAgent";
          const isCreateEventAgent = arg?.session?.agent_type === "CreateEventAgent";
          const isGoalSetAgent = arg?.session?.agent_type === "GoalSetAgent";
          const isCustomerProfileAgent =
            arg?.session?.agent_type === "CustomerProfileAgent" ||
            (arg?.session?.agent_type === "PlanMessageAgent" &&
              arg?.session?.notification_type === "contacts_missing_required_fields");
          let text = isCustomerProfileAgent
            ? formatCustomerProfileAgentText(res)
            : formatSuccessChatText(res, isCrmAgent);
          if (isCreateEventAgent && Boolean(res?.missing_fields_bool)) {
            text = appendCreateEventPrefill(text, res?.proposed_event);
            return `${text}__EVENT_FORM_REQUIRED__`;
          }
          if (isGoalSetAgent && Boolean(res?.goalset_form_required)) {
            text = appendGoalSetPrefill(text, res?.goalset_prefill);
            return `${text}__GOALSET_FORM_REQUIRED__`;
          }
          return text;
        }
        if (res?.status === "error") {
          throw new Error(String(res.message || "An error occurred"));
        }
        return response;
      },
      invalidatesTags: ['Chat'],
    }),

    createSupervisorCalendarGoalBookSession: builder.mutation({
      query: ({ userEmail, threadId }: { userEmail: string; threadId: string }) => ({
        url: '/goal_book_markdown_session',
        method: 'POST',
        body: {
          user_email: userEmail,
          thread_id: threadId,
        },
      }),
      invalidatesTags: ['Sessions', 'Chat'],
    }),

    createSdsUserDocument: builder.mutation({
      query: (payload: unknown) => ({
        url: '/sdsusercrud',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['TabbedDict'],
    }),

    updateSdsUserDocument: builder.mutation({
      query: (payload: unknown) => ({
        url: '/sdsusercrud',
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['TabbedDict'],
    }),

    deleteSdsUserDocument: builder.mutation({
      query: ({ id, subcollection }: { id: string; subcollection: string }) => ({
        url: `/sdsusercrud?id=${encodeURIComponent(id)}&subcollection=${encodeURIComponent(subcollection)}&operation=delete`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TabbedDict'],
    }),
  }),
});

export const {
  useSendChatMessageMutation,
  useCreateSupervisorCalendarGoalBookSessionMutation,
  useCreateSdsUserDocumentMutation,
  useUpdateSdsUserDocumentMutation,
  useDeleteSdsUserDocumentMutation,
} = apiSlice;
